import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { useTranslation } from 'react-i18next'
import { enumLabel } from '@/lib/enumLabel'
import { APP_FEATURE_KEYS } from '@/types'
import type { SubscriptionPackage } from '@/types'

type PackageFormData = Omit<SubscriptionPackage, 'id' | 'created_at' | 'updated_at'>

interface PackageFormProps {
  initialData?: SubscriptionPackage
  onSubmit: (data: PackageFormData) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

const tierOptions = ['free', 'family', 'care_pro', 'enterprise'] as const

export function PackageForm({ initialData, onSubmit, onCancel, loading }: PackageFormProps) {
  const { t } = useTranslation()
  const [submitError, setSubmitError] = useState('')
  const { register, handleSubmit, control, setValue, formState: { errors } } = useForm<PackageFormData>({
    defaultValues: initialData
      ? {
          tier: initialData.tier,
          name: initialData.name,
          description: initialData.description,
          monthly_price_eur: initialData.monthly_price_eur,
          yearly_price_eur: initialData.yearly_price_eur,
          max_caregivers: initialData.max_caregivers,
          features: initialData.features ?? {},
          is_active: initialData.is_active ?? true,
          display_order: initialData.display_order ?? 0,
        }
      : {
          tier: 'free',
          name: '',
          description: '',
          monthly_price_eur: 0,
          yearly_price_eur: 0,
          max_caregivers: null,
          features: {},
          is_active: true,
          display_order: 0,
        },
  })

  const features = useWatch({ control, name: 'features' }) ?? {}
  const isActive = useWatch({ control, name: 'is_active' })

  // Saving errors (e.g. refused by the database) are shown in the form instead of
  // failing silently.
  const submit = async (data: PackageFormData) => {
    setSubmitError('')
    try {
      await onSubmit(data)
    } catch (error) {
      console.error('Saving package failed:', error)
      setSubmitError(t('packages.form.saveFailed'))
    }
  }

  // An empty number field gives NaN; require a real, non-negative amount.
  const priceRules = {
    valueAsNumber: true as const,
    validate: (v: number) =>
      (Number.isFinite(v) && v >= 0) || t('packages.form.priceRequired'),
  }

  const toggleFeature = (key: string) => {
    const current = features[key]
    setValue('features', {
      ...features,
      [key]: { enabled: !current?.enabled, limit: current?.limit ?? null },
    })
  }

  const setFeatureLimit = (key: string, limit: string) => {
    const current = features[key]
    setValue('features', {
      ...features,
      [key]: { enabled: current?.enabled ?? false, limit: limit === '' ? null : Number(limit) },
    })
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-4">
      {submitError && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{submitError}</div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">{t('packages.form.tier')}</label>
          <select
            {...register('tier', { required: t('packages.form.tierRequired') })}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {tierOptions.map((tier) => (
              <option key={tier} value={tier}>
                {enumLabel(t, 'tiers', tier)}
              </option>
            ))}
          </select>
          {errors.tier && <p className="text-xs text-red-600">{errors.tier.message}</p>}
        </div>
        <Input
          id="name"
          label={t('packages.form.name')}
          {...register('name', { required: t('packages.form.nameRequired') })}
          error={errors.name?.message}
        />
      </div>

      <Input
        id="description"
        label={t('packages.form.description')}
        {...register('description')}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          id="monthly_price_eur"
          label={t('packages.form.monthlyPrice')}
          type="number"
          step="0.01"
          min="0"
          {...register('monthly_price_eur', priceRules)}
          error={errors.monthly_price_eur?.message}
        />
        <Input
          id="yearly_price_eur"
          label={t('packages.form.yearlyPrice')}
          type="number"
          step="0.01"
          min="0"
          {...register('yearly_price_eur', priceRules)}
          error={errors.yearly_price_eur?.message}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          id="max_caregivers"
          label={t('packages.form.maxCaregivers')}
          type="number"
          {...register('max_caregivers', {
            setValueAs: (v: string) => (v === '' ? null : Number(v)),
          })}
        />
        <Input
          id="display_order"
          label={t('packages.form.displayOrder')}
          type="number"
          {...register('display_order', {
            setValueAs: (v: string) => (v === '' || v === null ? 0 : Number(v)),
          })}
        />
      </div>

      <Toggle
        checked={isActive}
        onChange={(val) => setValue('is_active', val)}
        label={t('packages.form.active')}
      />

      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-3">{t('packages.form.features')}</p>
        <div className="space-y-3">
          {APP_FEATURE_KEYS.map((key) => (
            <div key={key} className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Toggle
                checked={features[key]?.enabled ?? false}
                onChange={() => toggleFeature(key)}
              />
              <span className="min-w-0 flex-1 text-sm text-gray-700 sm:w-36 sm:flex-none">
                {enumLabel(t, 'featureNames', key)}
              </span>
              <input
                type="number"
                placeholder={t('packages.form.limitPlaceholder')}
                value={features[key]?.limit ?? ''}
                onChange={(e) => setFeatureLimit(key, e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-xs sm:w-48"
                disabled={!features[key]?.enabled}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? t('common.saving') : initialData ? t('packages.form.update') : t('packages.form.create')}
        </Button>
      </div>
    </form>
  )
}
